/* eslint-disable @typescript-eslint/no-unused-vars */
import React from 'react';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { createWalletEntry, userSelector } from '../../../store/slices';
import { CreateWalletEntry, IUser, TransactionEntry } from '../../../types';
import { IOnChange, SelectInput } from './select';

const inputClass = 'bg-subtle border border-slate-medium rounded-lg px-3 py-3 text-sm text-text placeholder:text-text-placeholder focus:outline-none focus:bg-white focus:border-primary w-full';

interface UpdateWalletProps {
  close: () => void;
  user?: IUser;
}

export const UpdateWallet = ({ close, user }: UpdateWalletProps) => {
  const dispatch = useAppDispatch();
  const { usersUnpaginated } = useAppSelector(userSelector);

  const [payload, setPayload] = React.useState<CreateWalletEntry>({
    amount: 0,
    userId: user?.id ?? 0,
    entry: 'DEBIT',
    description: '',
    reference: '',
  });

  const handleUserChange: IOnChange<IUser> = (selectedUser) => {
    setPayload({ ...payload, userId: (selectedUser as IUser).id });
  };

  const handleInputValue = (event: React.SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = event.currentTarget.value;
    const name = event.currentTarget.name;
    setPayload({ ...payload, [name]: name === 'amount' ? parseInt(value) : value });
  };

  const processTransaction = (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    dispatch(createWalletEntry({ payload, cb: close }));
  };

  return (
    <div className="bg-white p-6 rounded-2xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="bold text-xl text-text">Debit customer wallet</h2>
        <button onClick={close} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors">
          <svg width={16} height={16} viewBox="0 0 16 16" fill="none">
            <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>

      <form onSubmit={processTransaction}>
        {/* User search */}
        {!user && (
          <div className="mb-4">
            <label className="medium text-sm text-text block mb-1.5">Users</label>
            <SelectInput
              singleSelect
              name="user"
              label=""
              placeholder="Search user"
              options={usersUnpaginated}
              handleChange={handleUserChange}
              getOptionValue={(u) => u.phoneNumber}
              getOptionLabel={(u) => `${u.firstName} ${u.lastName}, ${u.phoneNumber}`}
            />
          </div>
        )}

        {/* Amount */}
        <div className="mb-4">
          <label className="medium text-sm text-text block mb-1.5">Enter amount</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-text-placeholder">₦</span>
            <input
              type="number"
              name="amount"
              value={payload.amount || ''}
              onChange={handleInputValue}
              placeholder="0.00"
              min={0}
              className={`${inputClass} pl-7`}
            />
          </div>
        </div>

        {/* Description */}
        <div className="mb-4">
          <label className="medium text-sm text-text block mb-1.5">Description</label>
          <textarea
            name="description"
            value={payload.description}
            onChange={handleInputValue}
            placeholder="Enter description"
            rows={3}
            className={`${inputClass} resize-none`}
          />
        </div>

        {/* Initiator */}
        {user && (
          <div className="mb-6">
            <label className="medium text-sm text-text block mb-1.5">Initiator</label>
            <input
              type="text"
              value={`${user.firstName} ${user.lastName}`}
              disabled
              className={inputClass}
            />
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end gap-3">
          <button type="button" onClick={close} className="px-6 py-2.5 text-sm medium border border-slate-medium rounded-lg hover:bg-gray-50 transition-colors">
            Cancel
          </button>
          <button
            type="submit"
            disabled={!(payload.userId && payload.amount && !!payload.description)}
            className={`px-6 py-2.5 text-sm medium rounded-lg text-white transition-colors ${
              payload.userId && payload.amount && !!payload.description
                ? 'bg-primary hover:opacity-90'
                : 'bg-primary-disabled cursor-not-allowed'
            }`}
          >
            Debit wallet
          </button>
        </div>
      </form>
    </div>
  );
};
